import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Light / dark / follow-the-system. Dark is the default (Obsidian is
 * dark-first). The choice is stored under `spade-theme`, which the inline
 * script in index.html reads before first paint so there's no flash.
 */
export type ThemePreference = 'dark' | 'light' | 'system';
export type ResolvedTheme = 'dark' | 'light';

const STORAGE_KEY = 'spade-theme';
const LIGHT_QUERY = '(prefers-color-scheme: light)';

interface ThemeContextValue {
  preference: ThemePreference;
  theme: ResolvedTheme;
  setPreference: (p: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function parsePreference(raw: string | null | undefined): ThemePreference {
  return raw === 'light' || raw === 'system' ? raw : 'dark';
}

export function resolveTheme(pref: ThemePreference, systemLight: boolean): ResolvedTheme {
  if (pref === 'system') return systemLight ? 'light' : 'dark';
  return pref;
}

function readStored(): ThemePreference {
  try {
    return parsePreference(localStorage.getItem(STORAGE_KEY));
  } catch {
    return 'dark';
  }
}

function systemPrefersLight(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(LIGHT_QUERY).matches
    : false;
}

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [preference, setPref] = useState<ThemePreference>(readStored);
  const [systemLight, setSystemLight] = useState<boolean>(systemPrefersLight);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(LIGHT_QUERY);
    const onChange = (): void => setSystemLight(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const theme = resolveTheme(preference, systemLight);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }, [theme]);

  const setPreference = useCallback((p: ThemePreference) => {
    setPref(p);
    try {
      localStorage.setItem(STORAGE_KEY, p);
    } catch {
      // Storage blocked: the choice still applies for this visit.
    }
  }, []);

  const value = useMemo(() => ({ preference, theme, setPreference }), [preference, theme, setPreference]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
